import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import axios from 'axios';
import { 
  Building2, User, FileText, Users, CheckCircle,
  Ambulance, Loader, FileCheck
} from 'lucide-react';

// Styled Components
const PageContainer = styled.div`
  background: linear-gradient(135deg, #fdfdfdff 0%, #764ba2 100%);
  min-height: 100vh;
  padding: 2rem 0;
`;

const RegistrationCard = styled(motion.div)`
  max-width: 1200px;
  margin: 0 auto;
  background: white;
  border-radius: 24px;
  box-shadow: 0 20px 60px -10px rgba(0, 0, 0, 0.3);
  overflow: hidden;
`;

const Header = styled.div`
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  color: white;
  padding: 3rem 2rem;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
    animation: pulse 3s ease-in-out infinite;
  }

  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 0.5; }
    50% { transform: scale(1.1); opacity: 0.3; }
  }

  h1 {
    margin: 0 0 1rem 0;
    font-size: 2.5rem;
    font-weight: 700;
    position: relative;
    z-index: 1;
  }

  p {
    margin: 0;
    font-size: 1.125rem;
    opacity: 0.95;
    position: relative;
    z-index: 1;
    max-width: 800px;
    margin: 0 auto;
  }
`;

const FormSection = styled.div`
  padding: 3rem 2rem;
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 3rem;
  gap: 0.5rem;
`;

const StepDot = styled.div`
  width: ${props => props.active ? '32px' : '12px'};
  height: 12px;
  border-radius: ${props => props.active ? '6px' : '50%'};
  background: ${props => props.active ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#e2e8f0'};
  transition: all 0.3s ease;
`;

const SectionTitle = styled.h3`
  color: #1e293b;
  font-size: 1.25rem;
  font-weight: 600;
  margin: 2rem 0 1.5rem 0;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  svg {
    color: #3b82f6;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: ${props => props.cols === 1 ? '1fr' : props.cols === 3 ? '1fr 1fr 1fr' : '1fr 1fr'};
  gap: 1.5rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  color: #475569;
  font-weight: 500;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.025em;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid ${props => props.error ? '#ef4444' : '#e2e8f0'};
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.2s ease;
  background: white;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid ${props => props.error ? '#ef4444' : '#e2e8f0'};
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.2s ease;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 2rem;
  margin-top: 0.5rem;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  color: #475569;

  input[type="radio"] {
    width: 20px;
    height: 20px;
    accent-color: #3b82f6;
    cursor: pointer;
  }
`;

const CheckboxGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 0.5rem;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  color: #475569;
  padding: 0.5rem;
  border-radius: 8px;
  transition: background 0.2s;

  &:hover {
    background: #f8fafc;
  }

  input[type="checkbox"] {
    width: 20px;
    height: 20px;
    accent-color: #3b82f6;
    cursor: pointer;
  }
`;

const TermsBox = styled.div`
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  border-radius: 12px;
  padding: 1.5rem;
  margin: 2rem 0;
`;

const TermsCheckbox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;

  input[type="checkbox"] {
    width: 24px;
    height: 24px;
    accent-color: #3b82f6;
    cursor: pointer;
    margin-top: 2px;
  }

  label {
    flex: 1;
    color: #1e293b;
    line-height: 1.6;

    a {
      color: #3b82f6;
      font-weight: 600;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 3rem;
`;

const SubmitButton = styled(motion.button)`
  padding: 1rem 3rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px -10px rgba(102, 126, 234, 0.5);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Alert = styled(motion.div)`
  padding: 1rem 1.5rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  ${props => props.variant === 'error' && `
    background: #fef2f2;
    color: #991b1b;
    border: 1px solid #fecaca;
  `}
  
  ${props => props.variant === 'success' && `
    background: #f0fdf4;
    color: #166534;
    border: 1px solid #bbf7d0;
  `}
`;

const SuccessContainer = styled(motion.div)`
  text-align: center;
  padding: 4rem 2rem;
`;




const SuccessIcon = styled(motion.div)`
  width: 120px;
  height: 120px;
  margin: 0 auto 2rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  svg {
    width: 60px;
    height: 60px;
    color: white;
  }

  &::before {
    content: '';
    position: absolute;
    inset: -20px;
    border-radius: 50%;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    opacity: 0.2;
    animation: ripple 2s ease-out infinite;
  }

  &::after {
    content: '';
    position: absolute;
    inset: -40px;
    border-radius: 50%;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    opacity: 0.1;
    animation: ripple 2s ease-out infinite 0.5s;
  }

  @keyframes ripple {
    0% { transform: scale(1); opacity: 0.3; }
    100% { transform: scale(1.2); opacity: 0; }
  }
`;

const Confetti = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  height: 100%;
  pointer-events: none;

  span {
    position: absolute;
    width: 10px;
    height: 10px;
    animation: confetti-fall 3s linear forwards;
    
    @keyframes confetti-fall {
      0% {
        transform: translateY(-100vh) rotate(0deg);
        opacity: 1;
      }
      100% {
        transform: translateY(100vh) rotate(720deg);
        opacity: 0;
      }
    }
  }
`;

const classifications = [
  { value: 'gov_hospital', label: "Gov't Hospital" },
  { value: 'private_hospital', label: 'Private Hospital' },
  { value: 'health_center', label: 'Health Center' },
  { value: 'specialty_medical_center', label: 'Specialty Medical Center' },
  { value: 'ambulance_care_provider', label: 'Ambulance Care Provider' },
  { value: 'diagnostic_center', label: 'Diagnostic Center' },
  { value: 'other', label: 'Other' },
];

const subcities = ['Arada','Addis Ketema','Bole','Gulelle','Kirkose','Kalite','Kolfe','Lideta','Lafto','Lemi Kura','Yeka'];

const staffRoleOptions = [
  { value: 'paramedics', label: 'Paramedics' },
  { value: 'emt', label: 'EMT' },
  { value: 'physician', label: 'Physician' },
  { value: 'nurse', label: 'Nurse' },
  { value: 'anesthetists', label: 'Anesthetists' },
  { value: 'health_officers', label: 'Health Officers' },
  { value: 'other', label: 'Other' },
];

const documentOptions = [
  { value: 'licenses', label: 'Licenses' },
  { value: 'car_ownership_certificate', label: 'Car ownership certificate' },
  { value: 'insurance_certificate', label: 'Insurance certificate' },
];

const AmbulanceCompanyRegistration = () => {
  const [form, setForm] = useState({
    company_name: '',
    institution_classification: '',
    subcity: '',
    woreda: '',
    location_gis: '',
    contact_person: '',
    contact_email: '',
    contact_phone: '',
    contact_phone_2: '',
    address: '',
    branches_count: '',
    provides_ambulance: false,
    bls_count: '',
    als_count: '',
    transport_only_count: '',
    provides_prehospital: false,
    staff_roles: [],
    has_call_center: false,
    documents: [],
    wants_to_join: true,
    submission_date: new Date().toISOString().slice(0,10),
    agreeToTerms: false,
    terms_version: 'v1'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleMultiCheckbox = (name, optionValue, checked) => {
    setForm(prev => {
      const curr = new Set(prev[name] || []);
      if (checked) curr.add(optionValue); else curr.delete(optionValue);
      return { ...prev, [name]: Array.from(curr) };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const missing = [];

    if (!form.company_name) missing.push('Name of the institution');
    if (!form.institution_classification) missing.push('Classification of the institution');
    if (!form.subcity) missing.push('Subcity');
    if (!form.woreda) missing.push('Woreda');
    if (!form.contact_person) missing.push('Contact Person');
    if (!form.contact_phone) missing.push('Contact Person Phone Number');
    if (!form.contact_email) missing.push('Institutional Email');

    const branchesProvided = form.branches_count !== '' && form.branches_count !== null && form.branches_count !== undefined;
    if (!branchesProvided) missing.push('How many branches do you have');

    // Documents are required at least one
    if (!Array.isArray(form.documents) || form.documents.length === 0) missing.push('Valid documents');

    // Required radios (we accept default values but ensure presence as boolean)
    const hasProvidesAmbulance = typeof form.provides_ambulance === 'boolean';
    const hasPrehospital = typeof form.provides_prehospital === 'boolean';
    const hasCallCenter = typeof form.has_call_center === 'boolean';
    const hasWantsToJoin = typeof form.wants_to_join === 'boolean';

    if (!hasProvidesAmbulance) missing.push('Do you provide an ambulance service');
    if (!hasPrehospital) missing.push('Do you provide prehospital care');
    if (!hasCallCenter) missing.push('Call center availability');
    if (!hasWantsToJoin) missing.push('Do you want to join "ለAmbulance"');

    if (!form.submission_date) missing.push('Submission Date');

    if (!form.agreeToTerms) {
      setError('You must agree to the Terms and Conditions');
      return;
    }

    if (form.provides_ambulance) {
      const bStr = form.bls_count;
      const aStr = form.als_count;
      const b = bStr === '' || bStr === null || bStr === undefined ? null : Number(bStr);
      const a = aStr === '' || aStr === null || aStr === undefined ? null : Number(aStr);
      if (b === null || isNaN(b)) missing.push('Functional BLS ambulances');
      if (a === null || isNaN(a)) missing.push('Functional ALS ambulances');
    }

    if (missing.length > 0) {
      setError(`Please fill the required fields: ${missing.join(', ')}`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const payload = {
      ...form,
      branches_count: branchesProvided ? Number(form.branches_count) : null,
      bls_count: form.bls_count === '' ? null : Number(form.bls_count),
      als_count: form.als_count === '' ? null : Number(form.als_count),
      transport_only_count: form.transport_only_count === '' ? null : Number(form.transport_only_count),
    };

    setLoading(true);
    try {
      await axios.post('/ambulance-companies/register', payload);
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const msg = err?.response?.data?.msg || err?.response?.data?.message || 'Failed to submit registration';
      setError(msg);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  // Success screen with confetti animation
  const renderSuccessScreen = () => (
    <SuccessContainer
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      <Confetti>
        {[...Array(50)].map((_, i) => (
          <span
            key={i}
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              background: ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'][Math.floor(Math.random() * 5)]
            }}
          />
        ))}
      </Confetti>
      
      <SuccessIcon
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
      >
        <CheckCircle />
      </SuccessIcon>
      
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        style={{ color: '#1e293b', marginBottom: '1rem', fontSize: '2rem', fontWeight: '700' }}
      >
        Registration Submitted Successfully!
      </motion.h2>
      
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        style={{ color: '#64748b', fontSize: '1.125rem', maxWidth: '600px', margin: '0 auto 2rem' }}
      >
        Thank you for registering with ለAmbulance Network. Our team will review your application and notify you by email within 2-3 business days.
      </motion.p>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}
      >
        <SubmitButton
          onClick={() => window.location.href = '/'}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <CheckCircle size={20} />
          Back to Home
        </SubmitButton>
      </motion.div>
    </SuccessContainer>
  );

  return (
    <PageContainer>
      <RegistrationCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Header>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Ambulance size={48} style={{ marginBottom: '1rem' }} />
            <h1>Ambulance Care Providers Registration</h1>
            <p>
              TirtaMed Plc, in collaboration with eTech technology company, presents a solution to establish an integrated first and second-tier ambulance network system through a mobile app and a call center.
            </p>
          </motion.div>
        </Header>

        <FormSection>
          <AnimatePresence mode="wait">
            {success ? (
              renderSuccessScreen()
            ) : (
              <motion.div
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <StepIndicator>
                  <StepDot active={currentStep >= 1} />
                  <StepDot active={currentStep >= 2} />
                  <StepDot active={currentStep >= 3} />
                </StepIndicator>

                {error && (
                  <Alert variant="error" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                    <FileCheck size={20} />
                    {error}
                  </Alert>
                )}

                <form onSubmit={handleSubmit}>
                  <SectionTitle>
                    <Building2 size={24} />
                    Institution Information
                  </SectionTitle>

                  <FormGrid cols={2}>
                    <FormGroup>
                      <Label>Name of the institution *</Label>
                      <Input
                        name="company_name"
                        value={form.company_name}
                        onChange={handleChange}
                        placeholder="Enter institution name"
                        required
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Number of branches</Label>
                      <Input
                        type="number"
                        min={0}
                        name="branches_count"
                        value={form.branches_count}
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </FormGroup>
                  </FormGrid>

                  <FormGrid cols={2}>
                    <FormGroup>
                      <Label>Classification of institution</Label>
                      <Select
                        name="institution_classification"
                        value={form.institution_classification}
                        onChange={handleChange}
                      >
                        <option value="">Select classification...</option>
                        {classifications.map(c => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                      </Select>
                    </FormGroup>
                    <FormGroup>
                      <Label>Subcity (Address)</Label>
                      <Select name="subcity" value={form.subcity} onChange={handleChange}>
                        <option value="">Select subcity...</option>
                        {subcities.map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </Select>
                    </FormGroup>
                  </FormGrid>

                  <FormGrid cols={2}>
                    <FormGroup>
                      <Label>Woreda</Label>
                      <Input
                        name="woreda"
                        value={form.woreda}
                        onChange={handleChange}
                        placeholder="Enter woreda"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Location (GIS Coordinates)</Label>
                      <Input
                        name="location_gis"
                        value={form.location_gis}
                        onChange={handleChange}
                        placeholder="e.g., 9.0103,38.7612"
                      />
                    </FormGroup>
                  </FormGrid>

                  <SectionTitle>
                    <User size={24} />
                    Contact Information
                  </SectionTitle>

                  <FormGrid cols={2}>
                    <FormGroup>
                      <Label>Contact Person</Label>
                      <Input
                        name="contact_person"
                        value={form.contact_person}
                        onChange={handleChange}
                        placeholder="Enter contact person name"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Institutional Email *</Label>
                      <Input
                        type="email"
                        name="contact_email"
                        value={form.contact_email}
                        onChange={handleChange}
                        placeholder="contact@institution.com"
                        required
                      />
                    </FormGroup>
                  </FormGrid>

                  <FormGrid cols={3}>
                    <FormGroup>
                      <Label>Contact Phone *</Label>
                      <Input
                        name="contact_phone"
                        value={form.contact_phone}
                        onChange={handleChange}
                        placeholder="+251911000000"
                        required
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Alternate Phone</Label>
                      <Input
                        name="contact_phone_2"
                        value={form.contact_phone_2}
                        onChange={handleChange}
                        placeholder="+251911000001"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Address</Label>
                      <Input
                        name="address"
                        value={form.address}
                        onChange={handleChange}
                        placeholder="Enter full address"
                      />
                    </FormGroup>
                  </FormGrid>

                  <SectionTitle>
                    <Ambulance size={24} />
                    Ambulance Services
                  </SectionTitle>

                  <FormGrid cols={2}>
                    <FormGroup>
                      <Label>Do you provide ambulance service?</Label>
                      <RadioGroup>
                        <RadioLabel>
                          <input
                            type="radio"
                            name="provides_ambulance"
                            checked={!!form.provides_ambulance}
                            onChange={() => setForm(prev => ({...prev, provides_ambulance: true}))}
                          />
                          Yes
                        </RadioLabel>
                        <RadioLabel>
                          <input
                            type="radio"
                            name="provides_ambulance"
                            checked={!form.provides_ambulance}
                            onChange={() => setForm(prev => ({...prev, provides_ambulance: false, bls_count:'', als_count:'', transport_only_count:''}))}
                          />
                          No
                        </RadioLabel>
                      </RadioGroup>
                    </FormGroup>
                    <FormGroup>
                      <Label>Do you provide prehospital care?</Label>
                      <RadioGroup>
                        <RadioLabel>
                          <input
                            type="radio"
                            name="provides_prehospital"
                            checked={!!form.provides_prehospital}
                            onChange={() => setForm(prev => ({...prev, provides_prehospital: true}))}
                          />
                          Yes
                        </RadioLabel>
                        <RadioLabel>
                          <input
                            type="radio"
                            name="provides_prehospital"
                            checked={!form.provides_prehospital}
                            onChange={() => setForm(prev => ({...prev, provides_prehospital: false}))}
                          />
                          No
                        </RadioLabel>
                      </RadioGroup>
                    </FormGroup>
                  </FormGrid>

                  {form.provides_ambulance && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      transition={{ duration: 0.3 }}
                    >
                      <FormGrid cols={3}>
                        <FormGroup>
                          <Label>Functional BLS Ambulances</Label>
                          <Input
                            type="number"
                            min={0}
                            name="bls_count"
                            value={form.bls_count}
                            onChange={handleChange}
                            placeholder="0"
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label>Functional ALS Ambulances</Label>
                          <Input
                            type="number"
                            min={0}
                            name="als_count"
                            value={form.als_count}
                            onChange={handleChange}
                            placeholder="0"
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label>Transport-only Ambulances</Label>
                          <Input
                            type="number"
                            min={0}
                            name="transport_only_count"
                            value={form.transport_only_count}
                            onChange={handleChange}
                            placeholder="0"
                          />
                        </FormGroup>
                      </FormGrid>
                    </motion.div>
                  )}

                  <SectionTitle>
                    <Users size={24} />
                    Staff & Operations
                  </SectionTitle>

                  <FormGrid cols={2}>
                    <FormGroup>
                      <Label>Staff working in ambulances</Label>
                      <CheckboxGroup>
                        {staffRoleOptions.map(opt => (
                          <CheckboxLabel key={opt.value}>
                            <input
                              type="checkbox"
                              checked={form.staff_roles.includes(opt.value)}
                              onChange={(e) => handleMultiCheckbox('staff_roles', opt.value, e.target.checked)}
                            />
                            {opt.label}
                          </CheckboxLabel>
                        ))}
                      </CheckboxGroup>
                    </FormGroup>
                    <FormGroup>
                      <Label>Do you have a dedicated call center?</Label>
                      <RadioGroup>
                        <RadioLabel>
                          <input
                            type="radio"
                            name="has_call_center"
                            checked={!!form.has_call_center}
                            onChange={() => setForm(prev => ({...prev, has_call_center: true}))}
                          />
                          Yes
                        </RadioLabel>
                        <RadioLabel>
                          <input
                            type="radio"
                            name="has_call_center"
                            checked={!form.has_call_center}
                            onChange={() => setForm(prev => ({...prev, has_call_center: false}))}
                          />
                          No
                        </RadioLabel>
                      </RadioGroup>
                    </FormGroup>
                  </FormGrid>

                  <SectionTitle>
                    <FileText size={24} />
                    Documents & Agreement
                  </SectionTitle>

                  <FormGroup>
                    <Label>Valid documents you have</Label>
                    <CheckboxGroup>
                      {documentOptions.map(opt => (
                        <CheckboxLabel key={opt.value}>
                          <input
                            type="checkbox"
                            checked={form.documents.includes(opt.value)}
                            onChange={(e) => handleMultiCheckbox('documents', opt.value, e.target.checked)}
                          />
                          {opt.label}
                        </CheckboxLabel>
                      ))}
                    </CheckboxGroup>
                  </FormGroup>

                  <FormGrid cols={2}>
                    <FormGroup>
                      <Label>Want to join ለAmbulance Solution?</Label>
                      <RadioGroup>
                        <RadioLabel>
                          <input
                            type="radio"
                            name="wants_to_join"
                            checked={!!form.wants_to_join}
                            onChange={() => setForm(prev => ({...prev, wants_to_join: true}))}
                          />
                          Yes
                        </RadioLabel>
                        <RadioLabel>
                          <input
                            type="radio"
                            name="wants_to_join"
                            checked={!form.wants_to_join}
                            onChange={() => setForm(prev => ({...prev, wants_to_join: false}))}
                          />
                          No
                        </RadioLabel>
                      </RadioGroup>
                    </FormGroup>
                    <FormGroup>
                      <Label>Submission Date</Label>
                      <Input
                        type="date"
                        name="submission_date"
                        value={form.submission_date}
                        onChange={handleChange}
                      />
                    </FormGroup>
                  </FormGrid>

                  <TermsBox>
                    <TermsCheckbox>
                      <input
                        type="checkbox"
                        name="agreeToTerms"
                        checked={form.agreeToTerms}
                        onChange={handleChange}
                        id="agreeToTerms"
                      />
                      <label htmlFor="agreeToTerms">
                        I agree to the <a href="/terms" target="_blank" rel="noreferrer">Terms & Conditions</a> and confirm that all information provided is accurate and complete.
                      </label>
                    </TermsCheckbox>
                  </TermsBox>

                  <ButtonGroup>
                    <SubmitButton
                      type="submit"
                      disabled={loading}
                      whileHover={{ scale: loading ? 1 : 1.02 }}
                      whileTap={{ scale: loading ? 1 : 0.98 }}
                    >
                      {loading ? (
                        <>
                          <Loader className="spinning" size={20} />
                          Submitting Registration...
                        </>
                      ) : (
                        <>
                          <CheckCircle size={20} />
                          Submit Registration
                        </>
                      )}
                    </SubmitButton>
                  </ButtonGroup>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </FormSection>
      </RegistrationCard>
    </PageContainer>
  );
};

export default AmbulanceCompanyRegistration;
